import React from "react";
import {
  ChevronRight,
  BrainCog,
  Code2,
  Smartphone,
  Palette,
  BarChart,
  Zap,
  Dot
} from "lucide-react";

const services = [
  {
    image: "/images/7.png",
    icon: <BrainCog className="h-8 w-8 text-red-600" />,
    title: "Intelligent Automation",
    description:
      "Turn manual workflows into self-operating, decision-making systems. We automate complex business operations using AI-powered systems that learn, adapt, and scale. From internal efficiency to customer-facing experiences, our automation layer brings real ROI.",
    features: [
      "Conversational AI (chatbots, voice bots, AI agents)",
      "Automated workflows powered by machine learning",
      "Predictive analytics and real-time recommendations",
      "NLP-based document handling (summarization, classification, data extraction)",
      "RPA (robotic process automation) integrated with APIs",
    ],
    ideal:
      "Support centers, HR onboarding, healthcare workflows, B2B SaaS, customer feedback automation",
  },
  {
    image: "/images/8.png",
    icon: <Code2 className="h-8 w-8 text-red-500" />,
    title: "AI-Augmented Software Engineering",
    description:
      "Custom software built for performance—with AI baked in. Our engineers design robust backends, intelligent APIs, and scalable infrastructure using AI to power logic, decision-making, and data processing.",
    features: [
      "Full-cycle backend development (Rails, Node.js, Python, Go)",
      "API-first architecture (REST, GraphQL, Webhooks)",
      "Microservices, event-driven systems, background job processing",
      "AI-enhanced modules: scoring systems, rule engines, fraud detection",
      "Data pipelines for training models or streaming analytics",
    ],
    ideal:
      "Enterprise software, marketplace platforms, logistics systems, admin panels, dashboards",
  },
  {
    image: "/images/9.png",
    icon: <Smartphone className="h-8 w-8 text-green-600" />,
    title: "Web & Mobile App Development",
    description:
      "Crafting smart, scalable, multi-platform apps for modern users. We build mobile-first, performance-driven apps enriched with AI-powered features like personalization, smart search, auto-fill, and content generation.",
    features: [
      "Cross-platform apps with React Native, Flutter, Next.js",
      "AI-powered onboarding flows and chat-based experiences",
      "Supabase/PostgreSQL integrations with smart filters & dashboards",
      "Payment gateways, geolocation, file upload, push notifications",
      "App store-ready deployment with analytics tracking",
    ],
    ideal:
      "Startups, direct-to-consumer (D2C), healthcare platforms, job portals, social products",
  },
  {
    image: "/images/ui-ux-design.webp",
    icon: <Palette className="h-8 w-8 text-indigo-500" />,
    title: "AI-Centered UI/UX Design",
    description:
      "Design that adapts to users, not just devices. We go beyond static wireframes by leveraging AI insights to create interactive, responsive, and goal-driven design systems.",
    features: [
      "User journey design with GenAI-powered wireframes",
      "Personalization logic: conditional UI components based on behavior",
      "UX writing with GPT-4 tailored microcopy",
      "A/B testing strategy enhanced by AI analytics",
      "Accessibility audits using computer vision and assistive rules",
    ],
    ideal:
      "SaaS tools, eLearning platforms, fintech apps, onboarding-heavy products",
  },
  {
    image: "/images/digital_process_automation.webp",
    icon: <BarChart className="h-8 w-8 text-yellow-500" />,
    title: "Digital Automation & Transformation",
    description:
      "Modernize outdated systems using intelligent digital layers. We replace your legacy tools with modern cloud-native platforms and integrate AI models where they drive the most value.",
    features: [
      "Legacy-to-cloud migration with AI workflow upgrades",
      "Real-time data processing with auto-decision layers",
      "Vision AI for image classification, OCR, facial/object detection",
      "Smart scheduling, pricing, and route optimization algorithms",
      "KPI dashboards with AI-generated insights",
    ],
    ideal:
      "Field services, insurance, telehealth, manufacturing, banking ops",
  },
  {
    image: "/images/AI-model-development.webp",
    icon: <Zap className="h-8 w-8 text-purple-500" />,
    title: "AI Consulting, Model Development & Deployment",
    description:
      "From idea to inference—we bring your AI use case to life. Our AI engineers help you identify the right use case, build proof of concept, fine-tune models, and deploy them at scale.",
    features: [
      "Use case discovery and AI feasibility workshops",
      "Prompt engineering and GPT/LLM fine-tuning",
      "RAG architecture (LangChain + Pinecone + Vector DBs)",
      "Deployment on AWS SageMaker, Azure ML, or local GPU",
      "MLOps: version control, monitoring, CI/CD for AI pipelines",
    ],
    ideal:
      "Internal tools, knowledge bots, reporting engines, compliance automation, customer retention models",
  },
];

const InnovativeServices = () => {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-10 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-corporate-gray-dark mb-3 sm:mb-4">
            AI-First Digital Engineering.{" "}
            <span className="text-corporate-red">Full-Cycle Innovation.</span>
          </h2>
          <p className="text-base sm:text-lg text-corporate-gray max-w-2xl sm:max-w-3xl mx-auto">
            As a modern software development company, SR NEXT merges full-stack
            expertise with cutting-edge AI to deliver scalable, intelligent, and
            future-ready digital solutions—from concept to deployment.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-8">
          {services.map((service) => (
            <div
              key={service.title}
              className="bg-white rounded-2xl shadow-md hover:shadow-lg border border-gray-100 flex flex-col lg:flex-row items-stretch lg:gap-6 overflow-hidden transition-all duration-300"
            >
              <img
                src={service.image}
                alt={service.title}
                className="w-full lg:w-64 h-48 lg:h-full object-cover object-center flex-shrink-0"
              />
              <div className="p-6 flex flex-col flex-1 justify-center">
                <div className="flex items-center gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-xl">
                    {service.icon}
                  </span>
                  <h3 className="text-lg sm:text-xl font-bold text-corporate-gray-dark">
                    {service.title}
                  </h3>
                </div>
                <p className="text-corporate-gray-dark text-base mb-3">
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {service.features.map((feature) => (
                    <span
                      key={feature}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-50 text-gray-700 text-xs font-medium shadow-sm border border-gray-100"
                    >
                      <Dot className="w-4 h-4 text-corporate-red" />
                      {feature}
                    </span>
                  ))}
                </div>
                <div className="text-xs text-gray-500 font-semibold mt-auto">
                  <span className="text-corporate-red">Ideal For:</span>{" "}
                  {service.ideal}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InnovativeServices;
